import { GzLandingPageWebComponent } from '../../../common/gzLandingPage';
import { CountdownTranslations } from '../../../../ui/gzCountDown/types';
import { html, TemplateResult } from 'lit';
import { InProductTrialCannotBeStartedReason } from '../../types/InProductTrialCannotBeStartedReason';
import { customElement, query, state } from 'lit/decorators.js';
import { AvailableOperation } from '../../types/AvailableOperation';
import { classMap } from 'lit/directives/class-map.js';
import { ProductLandingPageBuyConfig } from '../../types/ProductLandingPageBuyConfig';
import { ButtonColors, ButtonTypes } from '../../../../ui/gzButton/types';
import { WEB_COMPONENT_GZ_BUY_PRODUCT_CARD } from '../../../../webcomponents.constants';
import { GzBuyProductCardWebComponent } from '../../buyProductCard';

@customElement('gz-landing-page')
export class B2bLandingPageWebComponent extends GzLandingPageWebComponent {
  @state()
  private buyConfig: ProductLandingPageBuyConfig;

  @state()
  private availableOperation?: AvailableOperation;

  @query(WEB_COMPONENT_GZ_BUY_PRODUCT_CARD)
  buyProductCard: GzBuyProductCardWebComponent;

  public setAvailableOperation(visibleFor?: AvailableOperation): void {
    this.availableOperation = visibleFor;
  }

  public setCannotBeStartedReason(cannotBeStartedReason?: InProductTrialCannotBeStartedReason): void {
    this.cannotBeStartedReason = cannotBeStartedReason;
  }

  public setBuyConfig(buyConfig: ProductLandingPageBuyConfig): void {
    this.buyConfig = buyConfig;

    if (buyConfig.enabled && this.buyProductCard) {
      // ensure buy card is reloaded even if product type does not change (user clicks again on same product)
      this.buyProductCard.requestUpdate('productType');
    }
  }

  protected renderCountDown(translations?: CountdownTranslations): TemplateResult | undefined {
    const isCountDownAllowedForReason =
      this.cannotBeStartedReason === InProductTrialCannotBeStartedReason.AlreadyStartedForCurrentProduct ||
      this.cannotBeStartedReason === InProductTrialCannotBeStartedReason.CoolDownNotPassedForStart;
    if (!isCountDownAllowedForReason || !this.dateToTimestamp) {
      return;
    }
    const component = this.createCountDownWebComponent(translations);
    return html`${component}`;
  }

  protected renderProduct() {
    if (!this.productItem) {
      return;
    }

    const classes = classMap({
      'is-start-available':
        this.availableOperation === AvailableOperation.Start &&
        this.cannotBeStartedReason !== InProductTrialCannotBeStartedReason.AlreadyStartedForCurrentProduct,
      'is-stop-available':
        this.availableOperation === AvailableOperation.Start &&
        this.cannotBeStartedReason === InProductTrialCannotBeStartedReason.AlreadyStartedForCurrentProduct,
      'is-request-available': this.availableOperation === AvailableOperation.Request,
      'is-demo-available': !!this.productItem.isDemoAvailable,
      'is-how-to-available': !!this.productItem.isHowToAvailable,
    });

    const tooltipText = this.cannotBeStartedReason
      ? this.productItem.tooltipTextMapping[this.cannotBeStartedReason]
      : '';

    const startButtonMustBeDisabled =
      this.cannotBeStartedReason !== undefined &&
      this.cannotBeStartedReason !== InProductTrialCannotBeStartedReason.AlreadyStartedForCurrentProduct;
    const requestButtonMustBeDisabled = this.cannotBeStartedReason !== undefined;

    return html`
      <gz-trial-product-item
        productTitle="${this.productItem.title}"
        description="${this.productItem.description}"
        imageAlignment="${this.productItem.imageAlignment}"
        imageSrc="${this.productItem.imageSrc}"
      >
        <gz-tooltip tooltipText=${tooltipText} class="trial-start-btn ${classes}">
          <gz-button
            .color=${ButtonColors.PRIMARY}
            .type=${ButtonTypes.OUTLINE}
            @click="${this.handleProductClicked}"
            id=${this.productItem.button1?.buttonId}
            ?disabled="${startButtonMustBeDisabled}"
            >${this.productItem.button1?.buttonText}
          </gz-button>
        </gz-tooltip>
        <gz-tooltip tooltipText=${tooltipText} class="trial-request-btn ${classes}">
          <gz-button
            @click="${this.handleProductClicked}"
            id=${this.productItem.button5?.buttonId}
            ?disabled="${requestButtonMustBeDisabled}"
            >${this.productItem.button5?.buttonText}
          </gz-button>
        </gz-tooltip>
        <gz-button
          .color=${ButtonColors.SECONDARY}
          .type=${ButtonTypes.QUIET}
          class="view-demo-btn ${classes}"
          @click="${this.handleProductClicked}"
          id=${this.productItem.button2?.buttonId}
          >${this.productItem.button2?.buttonText}
        </gz-button>
        <gz-button
          .color=${ButtonColors.SECONDARY}
          .type=${ButtonTypes.QUIET}
          class="how-to-btn ${classes}"
          @click="${this.handleProductClicked}"
          id=${this.productItem.button3?.buttonId}
          >${this.productItem.button3?.buttonText}
        </gz-button>
        <gz-button
          @click="${this.handleProductClicked}"
          type="outline"
          color="negative"
          id=${this.productItem.button4?.buttonId}
          class="trial-stop-btn ${classes}"
          >${this.productItem.button4?.buttonText}
        </gz-button>
      </gz-trial-product-item>
    `;
  }

  protected renderBuyProductCard(): TemplateResult | undefined {
    if (!this.buyConfig || !this.buyConfig.enabled) {
      return undefined;
    }

    return html`<div>
      <gz-buy-product-card
        .productName=${this.productItem?.title}
        .productType=${this.productItem?.type}
        .dataProvider=${this.buyConfig.dataProvider}
      ></gz-buy-product-card>
    </div>`;
  }
}
